/**
 * Evidence panel (PRD §5.4 M-03 + §5.5 U-04, U-07).
 *
 * Assembles four data sources for one incident:
 *   1. Signal waveform (signed URL → wavesurfer.js)
 *   2. Classifier output (event_type, confidence, reasoning, recommendation)
 *   3. Frozen context snapshot (work permits / trains / weather / baseline)
 *   4. GIS reference (segment_id + corridor — links to map)
 *
 * BM/EN reasoning toggle (U-04, U-09) is a placeholder until i18n lands.
 */

import { WaveformViewer } from "@/components/waveform/WaveformViewer";

export interface IncidentForPanel {
  id: string;
  event_type: string;
  confidence: number;
  reasoning: string;
  recommendedAction: string;
  modelVersion: string;
  signalWindowRef: string | null;
  eventTimestamp: string;
  siteId: string;
  segmentId: string;
  contextSnapshot: {
    segment_baseline_energy_rms: number | null;
    active_work_permits: Array<{
      permit_name: string;
      contractor: string | null;
      start_time: string;
      end_time: string;
    }>;
    train_schedule_window: Array<{
      train_id: string | null;
      direction: string | null;
      scheduled_at: string;
    }>;
    weather_condition: string | null;
  };
}

function confidenceColour(c: number): string {
  if (c >= 0.8) return "text-emerald-400";
  if (c >= 0.5) return "text-amber-300";
  return "text-rose-400";
}

export function EvidencePanel({
  incident,
  signedUrl,
}: {
  incident: IncidentForPanel;
  signedUrl?: string | null;
}) {
  const ctx = incident.contextSnapshot;
  return (
    <div className="space-y-4" data-testid="evidence-panel">
      {/* AI reasoning */}
      <section className="rounded-md border border-slate-800 bg-slate-900/40 p-3">
        <header className="flex items-center justify-between text-xs text-slate-400 uppercase tracking-wide mb-2">
          <span>AI reasoning</span>
          <span className="text-slate-500">{incident.modelVersion}</span>
        </header>
        <div className="flex items-center gap-3 mb-2">
          <span className="text-sm font-semibold">{incident.event_type}</span>
          <span
            className={`text-sm font-mono ${confidenceColour(incident.confidence)}`}
            data-testid="evidence-confidence-pct"
          >
            {Math.round(incident.confidence * 100)}%
          </span>
        </div>
        <p className="text-sm text-slate-200 leading-relaxed">
          {incident.reasoning}
        </p>
        <div className="mt-2 text-xs text-slate-400">
          <span className="text-slate-500">Recommended action:</span>{" "}
          {incident.recommendedAction}
        </div>
      </section>

      {/* Signal waveform */}
      <section className="rounded-md border border-slate-800 bg-slate-900/40 p-3">
        <header className="text-xs text-slate-400 uppercase tracking-wide mb-2">
          Signal waveform
        </header>
        <WaveformViewer
          signedUrl={signedUrl ?? incident.signalWindowRef ?? null}
          eventTimestamp={incident.eventTimestamp}
        />
      </section>

      {/* Context (frozen at classification time) */}
      <section
        className="rounded-md border border-slate-800 bg-slate-900/40 p-3"
        data-testid="context-snapshot"
      >
        <header className="text-xs text-slate-400 uppercase tracking-wide mb-2">
          Context at time of detection
        </header>
        <dl className="grid grid-cols-2 gap-2 text-xs">
          <dt className="text-slate-500">Site</dt>
          <dd className="text-slate-200">{incident.siteId}</dd>
          <dt className="text-slate-500">Segment</dt>
          <dd className="text-slate-200">{incident.segmentId}</dd>
          <dt className="text-slate-500">Weather</dt>
          <dd className="text-slate-200">{ctx.weather_condition ?? "—"}</dd>
          <dt className="text-slate-500">7-day baseline RMS</dt>
          <dd className="text-slate-200 font-mono">
            {ctx.segment_baseline_energy_rms?.toFixed(3) ?? "—"}
          </dd>
          <dt className="text-slate-500">Active work permits</dt>
          <dd className="text-slate-200">
            {ctx.active_work_permits.length === 0
              ? "none"
              : ctx.active_work_permits.map((p) => p.permit_name).join(", ")}
          </dd>
          <dt className="text-slate-500">Trains in window</dt>
          <dd className="text-slate-200">
            {ctx.train_schedule_window.length === 0
              ? "none"
              : ctx.train_schedule_window
                  .map((t) => t.train_id ?? "—")
                  .join(", ")}
          </dd>
        </dl>
      </section>
    </div>
  );
}
